/**
 * PageShell — the ONE place page chrome is composed (F4).
 *
 * THE DEFECT THIS CLOSES. Chrome — the navbar, the desktop `Footer`, the
 * mobile `MobileBottomNav` and the `pb-16` spacer that nav owes — was
 * hand-composed in ELEVEN layout modules, and no two agreed. Measured on the
 * hermetic fleet at 393x852: the account listing OVERVIEW and the section
 * EDITOR both rendered the public footer AND the tab bar; the listings hub
 * paid a `pb-16 md:pb-0` spacer for a nav it never rendered; `estate-detail`
 * dropped the footer its siblings keep. Every one of those is the same bug —
 * a policy nobody wrote down, restated eleven times.
 *
 * `detail-layout.tsx`'s header asked for a lint ("renders `MobileBottomNav`
 * => its `main` has `pb-16`"). This is stronger: the spacer is DERIVED from
 * the same boolean that renders the nav, in one table row, so the pair cannot
 * disagree — and outside this directory the chrome components are not
 * importable at all (`no-restricted-imports` in the app's eslint config), so no
 * module can mint a twelfth composition.
 *
 * ## Why it lives HERE and not in `src/app/`
 *
 * Because `root-layout.tsx` renders it, and the generated client route table
 * — which imports `root-layout` — is compiled by the SAME Bazel target as
 * every other `src/app/*` module (`src_app_entry`, tsbuildgen's Layer 7). A
 * `PageShell.tsx` in `src/app/` therefore closes a target CYCLE:
 * `src_app_root_layout` -> `src_app_entry` -> `src_app_root_layout`. It is
 * also the honest address: this element type IS "page chrome" in the app's
 * own DAG (`src/shared/components/{layout,realtime}/**` = `infra`), and what
 * this file does is compose page chrome. The `shell` still owns the
 * composition — `root-layout.tsx` renders exactly this one component.
 *
 * ## Where the answer comes from
 *
 * `handle.chrome`, emitted STATICALLY on the route object by `webroutesgen`
 * from `web/manifests/routes.yaml`'s `chrome:` — on the layout GROUP, which
 * covers its subtree, and on the leaves that override it. Static, not through
 * the `lazy()` module: React Router would take a `handle` from a lazy module,
 * but then the shell would not know the chrome until that chunk resolved — a
 * chrome FLASH on first paint and a different answer on the server than in
 * hydration, in an app whose architecture bans viewport state in JS for
 * exactly that reason.
 *
 * ## Why the bottom edge is a table and not a judgement
 *
 * `Z_LAYERS` puts `actionBar` (z-30) UNDER `bottomNav` (z-40) deliberately —
 * an action bar must never hide the app's navigation. So a page that renders
 * both loses: the nav paints over the band, and on the listing editor that was
 * measured as `elementFromPoint` at Save's own centre hitting a nav tab. The
 * fix is not a layer swap; it is that a `focus` page renders no nav. That
 * is the `focus` row, and `page-chrome.spec.ts` asserts the pair is never
 * both visible on any representative route at either viewport.
 */
import { isPageBandTitleKey, type PageBandTitleKey } from '@/shared/i18n/page-band-titles_gen';
import { useTranslations, type TranslateFunction } from '@/shared/i18n/useTranslations';
import {
  CHROME_MOBILE_HEADERS_WITH_BACK_CONTROL,
  isPageChrome,
  PAGE_CHROME,
  type ChromeSpec,
  type PageChrome,
} from '@/shared/lib/page-chrome_gen';
import { PageBackTargetProvider } from '@/shared/providers/PageBackTargetProvider';
import { internalPath, type Route } from '@letsgethome/routes';
import { plainText, type LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { useMemo } from 'react';
import { Outlet, useMatches } from 'react-router';

import { SmartAppBannerMeta } from '../SeoHead';
import { Footer } from './Footer';
import { MobileBottomNav } from './MobileBottomNav';
import { MobileTopBar } from './MobileTopBar';
import { SiteNavbar } from './SiteNavbar';

/**
 * THE table and its row type, GENERATED — re-exported here because this is the
 * address every reader already knows, and because the one thing this file must
 * never become again is a place where a band is DECIDED.
 *
 * It was hand-written TypeScript until PR-R3, and its own header claimed a
 * second copy could not exist while `web/e2e/helpers/page-chrome.ts` carried
 * one, column for column, welded to nothing. Both files are now emitted from
 * one exhaustive `PageChrome::spec()` match in webroutesgen, so a kind that
 * gains a column gains it in both, and `CHROME_KINDS_WITH_BACK_TARGET` is a
 * PROJECTION of the `mobileHeader` column rather than a second declaration a
 * jsdom assertion had to hold equal to it.
 */
export { PAGE_CHROME, type ChromeSpec };

/**
 * `main`'s class list, DERIVED.
 *
 * `flex flex-col` on every kind, unconditionally: a `bottom`-only sticky band
 * (`StickyActionBar`) is offset upward from its FLOW position and never pushed
 * down, so on a page shorter than the viewport it needs `mt-auto` in a column
 * to sit at the bottom edge. Giving it to every kind is what lets any page
 * grow one without also having to change its layout's contract (the LET-120
 * argument, made total).
 *
 * `pb-16 md:pb-0` iff the tab bar renders: the nav is `fixed`, i.e. out of
 * flow, so without the spacer it occludes the last 64 px of the page. That
 * this reads `spec.bottomTabs` — the same value that renders the nav — is the
 * point of the table.
 */
export function pageChromeMainClassName(spec: ChromeSpec): string {
  return cn(
    'flex flex-col',
    // The VIEWPORT half. `svh`, and NOT `dvh`, because this estate removed a
    // `100dvh` bound once already and wrote down why
    // (`features/wizard/pages/layout.tsx`: the wizard shell WAS `h-[100dvh]`
    // with a height-bounded `main`, and both halves went because nothing
    // STATED the bound as a contract — so the editor rendering the same step
    // forms under `min-h-screen` did not honour it and Save landed 70 px below
    // the fold — and because "iOS's `100dvh`-vs-URL-bar jitter goes with the
    // unit"). Neither reason survives here, and the second is why the unit is
    // `svh`:
    //   * the contract IS stated, by the only thing that can state it — a
    //     column of this table, declared per route in the manifest and
    //     rendered by one component. `ListingsClient` is `h-full` BECAUSE the
    //     shell bounds it; a page that wants to scroll is simply not `canvas`;
    //   * a map/list canvas is viewport-BOUNDED in a way a scrolling form is
    //     not — its map pane and its results pane are the scroll containers,
    //     and the document scrolling under them is the defect;
    //   * `svh` is the SMALL viewport, i.e. the height with the browser UI
    //     SHOWN, and it is STATIC: it does not move as the URL bar collapses,
    //     so the `dvh` jitter is unrepresentable and the canvas can never be
    //     taller than what is on screen. The cost is the `lvh - svh` strip
    //     when the bar has collapsed — empty space under panes that scroll
    //     anyway, not content pushed out of reach.
    spec.viewport === 'fixed' ? fixedViewportClassName(spec) : 'min-h-screen',
    spec.bottomTabs && 'pb-16 md:pb-0'
  );
}

/**
 * A bounded `main`: the viewport MINUS whatever band the same row renders above
 * it, at each breakpoint.
 *
 * It is a subtraction rather than a flat `h-svh` because the bands above `main`
 * are IN FLOW. The desktop navbar is a sticky `<header>` and the mobile band is
 * a sticky `<header>` — neither is `fixed` and neither is out of flow — so a
 * `100svh` main under one makes the DOCUMENT `band + viewport` tall, which is
 * exactly the document scroll a bounded viewport exists to remove.
 *
 * Both halves are DERIVED from the row's own columns, not from which kinds
 * exist today:
 *
 *   * below `md` the navbar is `hidden`, so the only band above `main` is the
 *     mobile top band — subtracted iff `mobileHeader` renders one. Until the
 *     `panel` row this branch had no members and `PageShell.react.test.tsx`
 *     asserted the pair `viewport: 'fixed'` + `mobileHeader: 'back'` was EMPTY,
 *     with the note that "a kind wanting both must subtract first". This is
 *     that subtraction; the test now asserts the subtraction instead of the
 *     absence;
 *   * at `md+` the band is `md:hidden` and the navbar is back, so the navbar is
 *     subtracted iff the row renders one.
 *
 * `svh` and not `dvh` for the reason the caller records at length: the SMALL
 * viewport is static, so the bound does not jitter as the URL bar collapses.
 * The two heights are the CSS variables the bands publish
 * (the `size` roles of `manifests/design-tokens.yaml`), never the literals — the mobile
 * band was 56 px and the navbar 64 px, and `conversations/thread` hand-typed
 * `calc(100vh-4rem)` for the wrong one of them at the wrong breakpoint.
 */
function fixedViewportClassName(spec: ChromeSpec): string {
  return cn(
    spec.mobileHeader === 'back' ? 'h-[calc(100svh-var(--mobile-header-height))]' : 'h-svh',
    'overflow-hidden',
    spec.navbar === 'site' ? 'md:h-[calc(100svh-var(--navbar-height))]' : 'md:h-svh'
  );
}

/**
 * The DEEPEST matched route that declares a chrome.
 *
 * Deepest wins, so a leaf overrides its group (that is how `/profile` is a tab
 * root inside an otherwise chrome-less account subtree). React Router types
 * `handle` as `unknown`, so the narrowing is ours; an unrecognised value
 * THROWS rather than defaulting, because a default would render some chrome
 * for a route whose chrome nobody decided — the F4 defect with a new face.
 * `webroutesgen` refuses to emit a page whose chain declares nothing, so
 * reaching either throw means the generated table and this file disagree.
 *
 * Taken as a plain argument rather than read from a hook so both refusals are
 * assertable: a throw from a route ELEMENT is swallowed by React Router's own
 * error boundary, which would make the test observe an error page instead of
 * the exception, and the message is the whole value of the throw.
 */
export function pageChromeFromMatches(
  matches: readonly { readonly id: string; readonly handle: unknown }[]
): PageChrome {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'chrome' in handle) {
      const { chrome } = handle as { chrome: unknown };
      if (isPageChrome(chrome)) return chrome;
      throw new Error(
        `PageShell: route "${match?.id ?? '?'}" declares handle.chrome=${JSON.stringify(chrome)}, which is not a page-chrome kind.`
      );
    }
  }
  throw new Error(
    'PageShell: no matched route declares handle.chrome. Every layout group in web/manifests/routes.yaml public_render must carry `chrome:`.'
  );
}

/**
 * Where the mobile band's back control goes when there is NO history to pop.
 *
 * Two sources, deepest match first, and DATA wins over the declaration at the
 * same depth:
 *
 *   * `loaderData.backHref` — for a leaf whose parent depends on data. The
 *     listing detail is the case: a listing's hub is the hub of ITS OWN mode,
 *     which only the loaded listing knows. It arrives as an unknown string, so
 *     it goes through `internalPath` — the one sanctioned runtime-string→Route
 *     door, which refuses a protocol-relative or absolute URL, so a loader
 *     cannot turn the back control into an open redirect.
 *   * `handle.backTo` — the manifest's `back:` row, emitted STATICALLY by
 *     `webroutesgen` as a branded `Route`, for the reason `handle.chrome` is
 *     static: no flash, and the same answer on the server as in hydration.
 *
 * `null` when neither exists — which is the honest answer for a kind that
 * renders no band. For a kind that DOES, see [`pageBandBackTarget`]: `null`
 * there is a contradiction, not a value.
 */
export function pageBackTargetFromMatches(
  // Spelled inline, exactly like `pageChromeFromMatches`'s parameter: this is
  // react-router's match shape, not an entity, and its `id` is a ROUTE id.
  matches: readonly { readonly handle: unknown; readonly data: unknown }[]
): Route | null {
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const data: unknown = match?.data;
    if (typeof data === 'object' && data !== null && 'backHref' in data) {
      const { backHref } = data as { backHref: unknown };
      if (typeof backHref === 'string') {
        const safe = internalPath(backHref);
        if (safe !== null) return safe;
      }
    }
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'backTo' in handle) {
      const { backTo } = handle as { backTo: unknown };
      if (typeof backTo === 'string') return internalPath(backTo);
    }
  }
  return null;
}

/**
 * Does this row show the reader a way back, whoever draws the control?
 *
 * `CHROME_MOBILE_HEADERS_WITH_BACK_CONTROL` is the generator's own partition of
 * the `mobileHeader` column (`back` = the shell's band, `page` = the page's own
 * bar), emitted beside the table it partitions.
 */
export function carriesBackControl(spec: ChromeSpec): boolean {
  return (CHROME_MOBILE_HEADERS_WITH_BACK_CONTROL as readonly string[]).includes(spec.mobileHeader);
}

/**
 * The back target for a given chrome row: `null` iff the row carries no back
 * control at all, and a THROW when it carries one and nothing decided where it
 * goes.
 *
 * Both arms of the `mobileHeader` column are covered — `back` (the shell's own
 * `MobileTopBar`) and `page` (the browse canvas's `ListingsTopBar`). The second
 * is why the column has three values: while it was a boolean the canvas's
 * control was outside every contract here, and its destination was a
 * `routes.home()` literal in a feature file.
 *
 * ## Why this is not a silent skip
 *
 * The two chrome contracts were asymmetric, and only one of them said so. An
 * undecided CHROME throws (`pageChromeFromMatches`, above) because "render some
 * chrome for a route whose chrome nobody decided" is the F4 defect with a new
 * face. An undecided back TARGET rendered NOTHING and said nothing: the band
 * vanished, the page kept its layout, and a phone reader on a `focus` page had
 * no way back — the very absence obs. 7 was about — with no error anywhere.
 *
 * `webroutesgen` REQUIRES a `back:` on every page whose resolved chrome carries
 * a back control, so a manifest cannot reach this. What CAN is a loader `backHref`
 * that fails `internalPath` on a route whose chain declares no `backTo`, and a
 * generated table that disagrees with this file. Both are bugs, and a bug that
 * silently removes a navigation affordance is strictly worse than one that
 * names itself.
 *
 * Taken as plain arguments, like its two siblings, so the refusal is assertable
 * — a throw from a route ELEMENT is swallowed by React Router's own error
 * boundary, and the message is the whole value of the throw.
 */
export function pageBandBackTarget(
  spec: ChromeSpec,
  matches: readonly { readonly id: string; readonly handle: unknown; readonly data: unknown }[]
): Route | null {
  // The question is "does anything on this page go back?", so it is asked of
  // the GENERATOR's own partition of the column rather than of a hand list of
  // values — a `mobileHeader` value added there is judged here by existing.
  // (Also the only spelling available: the estate's `no-raw-wire-alias-string`
  // rule refuses a direct comparison against the third value.)
  if (!carriesBackControl(spec)) return null;
  const target = pageBackTargetFromMatches(matches);
  if (target === null) {
    throw new Error(
      `PageShell: route "${matches[matches.length - 1]?.id ?? '?'}" carries a back control, but no matched route declares a back target. webroutesgen REQUIRES \`back:\` on every page whose chrome carries one; a loader may override it with \`backHref\`, which must be a same-app path.`
    );
  }
  return target;
}

/**
 * One match's loader data, read for the band's two lines — `null` when it
 * carries none.
 *
 * A BLANK value is not a value, in either line: a band showing the empty string
 * IS the R4-4 defect, and an empty second line is a band whose height claims a
 * subtitle that is not there. So both are trimmed and both fall through, which
 * is what lets a page carry a static `title:` AND a loader `bandTitle` — the
 * override only takes effect when the data actually says something.
 *
 * It arrives as an unknown string, so it is re-branded through `plainText` — the
 * sanctioned data mint, which is exactly what this is: user-facing text the
 * backend localised and the band shows verbatim.
 */
function bandTitleFromLoaderData(
  data: unknown
): { readonly title: LocalizedText; readonly subtitle?: LocalizedText } | null {
  if (typeof data !== 'object' || data === null || !('bandTitle' in data)) return null;
  const { bandTitle, bandSubtitle } = data as { bandTitle: unknown; bandSubtitle?: unknown };
  if (typeof bandTitle !== 'string' || bandTitle.trim() === '') return null;
  const subtitle =
    typeof bandSubtitle === 'string' && bandSubtitle.trim() !== '' ? bandSubtitle : null;
  return {
    title: plainText(bandTitle),
    subtitle: subtitle === null ? undefined : plainText(subtitle),
  };
}

/**
 * What the mobile band is TITLED, and its optional second line — `null` iff the
 * shell draws no band. NEVER a throw for a data page: see the tail of this
 * function for the 500 that rule cost.
 *
 * ## Two sources, one resolution point
 *
 * Deepest match first, and DATA wins over the declaration at the same depth —
 * the same order, and the same reason, as `pageBackTargetFromMatches` above:
 *
 *   * `loaderData.bandTitle` — a `LocalizedText` the BACKEND localised (an
 *     estate's name, a district's, the counterpart of a thread). It arrives as
 *     an unknown string, so it is re-branded through `plainText` — the sanctioned
 *     data mint, which is what this is: user-facing text shown verbatim;
 *   * `handle.title` — the manifest's `title:` row, emitted STATICALLY by
 *     `webroutesgen` as a ui-strings KEY, for the reason `handle.chrome` is
 *     static: no flash, and the same answer on the server as in hydration. It is
 *     narrowed through the generator's own `isPageBandTitleKey` roster, never
 *     cast — the shape `pageChromeFromMatches` uses for `handle.chrome` — and
 *     rendered with `t.raw`, whose parameter IS `MessageKeys<''>`.
 *
 * A BLANK loader value is not a value: a band showing the empty string is the
 * R4-4 defect itself, so an empty or whitespace-only `bandTitle` falls through to
 * the declaration exactly as an absent one does. That is what lets a page carry
 * both — the owner surfaces and the conversation thread do, because their data
 * title is genuinely absent for a draft with no address and for a support thread
 * with no counterpart, while their fallbacks are TRANSLATIONS no loader can mint.
 *
 * ## Why a THROW and not a blank band
 *
 * The same argument as the back target's, and the same generator guarantee
 * behind it: `webroutesgen` REFUSES a band-bearing page with neither a resolved
 * `title:` nor a loader, and asserts `bandTitle` as a TYPE on every band-bearing
 * page no `title:` covers. So a manifest cannot reach this and a loader that
 * forgets the field does not compile. What CAN reach it is a generated table that
 * disagrees with this file — a bug, and a bug that silently blanks the only line
 * of orientation a phone reader has is worse than one that names itself.
 *
 * Taken as plain arguments — the translator included — like its two siblings, so
 * the refusal is assertable: a throw from a route ELEMENT is swallowed by React
 * Router's own error boundary, and the message is the whole value of the throw.
 */
export function pageBandTitle(
  spec: ChromeSpec,
  matches: readonly { readonly id: string; readonly handle: unknown; readonly data: unknown }[],
  t: TranslateFunction<''>
): { readonly title: LocalizedText; readonly subtitle?: LocalizedText } | null {
  // The SHELL's own band, not the wider "is there a way back?" partition: a
  // `page` row draws its own top bar and everything in it, so a title there
  // would be a declaration nothing renders.
  if (spec.mobileHeader !== 'back') return null;
  for (let i = matches.length - 1; i >= 0; i -= 1) {
    const match = matches[i];
    const fromData = bandTitleFromLoaderData(match?.data);
    if (fromData !== null) return fromData;
    const handle: unknown = match?.handle;
    if (typeof handle === 'object' && handle !== null && 'title' in handle) {
      const { title } = handle as { title: unknown };
      if (isPageBandTitleKey(title)) return { title: renderBandTitleKey(t, title) };
      throw new Error(
        `PageShell: route "${match?.id ?? '?'}" declares handle.title=${JSON.stringify(title)}, which is not a page-band title key.`
      );
    }
  }
  // Nothing in the chain said what this band is titled. Since 2026-09-15 that is
  // NOT a throw, in either of the two shapes it takes:
  //
  //   * the deepest match produced NO loader data — its loader THREW (a 404
  //     slug, an auth redirect) and React Router is rendering that route's
  //     error boundary inside this shell. An ancestor group's `title:` still
  //     titles the band over the error page, which is the loop above; with no
  //     key anywhere in the chain there is no page to title and no band to draw;
  //   * it produced data whose `bandTitle` is EMPTY and no key covers it. This
  //     used to be a named refusal on the argument that the generated table
  //     could not disagree with this file — and the table could not, because
  //     the disagreement was never in the TYPE. `bandTitle: LocalizedText` says
  //     the field is present; an estate whose name projects to `''` for the
  //     request locale satisfied it and handed the shell nothing usable, the
  //     throw here took the SSR answer down, `/en/estates/135105-…` and
  //     `/en/listings/tsing-lung-tau-…` answered 500 on staging, and the
  //     prod-promote smoke gate refused four consecutive promotions on exactly
  //     those two URLs. So `webroutesgen` now REQUIRES a `title:` on every
  //     band-bearing page — the catalog is the one source that cannot be empty
  //     — and this arm is unreachable for any routed page. It stays a `null`
  //     rather than a throw because the cost of being wrong about that is a
  //     reader's 500, and a band with no text is strictly the smaller defect.
  return null;
}

/**
 * One catalog key, rendered.
 *
 * `t.raw` and NOT `t.dyn`: `dyn`'s parameter is `K & NonLiteralDynKey<K>`, and
 * `NonLiteralDynKey` resolves to `never` for a non-union `K` — it is the hatch
 * for keys COMPOSED from a bounded index, not for one transported out of a route
 * handle. `raw` is exactly "render this catalog key I am holding as a value".
 */
function renderBandTitleKey(t: TranslateFunction<''>, key: PageBandTitleKey): LocalizedText {
  return t.raw(key);
}

/**
 * The app shell's chrome band + the ONE `main` landmark.
 *
 * `main` is here and nowhere else — a `no-restricted-syntax` row makes a JSX
 * `<main>` outside this directory an error. Before this, eleven layouts each
 * rendered one and twenty page modules rendered a SECOND inside it, which is
 * why `web/e2e/helpers`' landmark assertion had to say `.first()` "for layouts
 * with nested mains".
 */
export function PageShell() {
  const matches = useMatches();
  // The NAMESPACELESS translator: the band's key comes out of a route handle, so
  // it is a full catalog key (`MessageKeys<''>`) and not a relative one. The
  // shell is under `IntlProvider` on the server render and in hydration alike,
  // which is the half round 2's R2a objection missed — loader DATA cannot call
  // `t()`, but the band is a component and always could.
  const t = useTranslations();
  const spec = PAGE_CHROME[pageChromeFromMatches(matches)];
  const backTarget = pageBandBackTarget(spec, matches);
  const band = pageBandTitle(spec, matches, t);
  // Boxed (see the provider) and memoised on the resolved target, so a page
  // reading it does not re-render on every shell render.
  const backTargetBox = useMemo(() => ({ target: backTarget }), [backTarget]);
  return (
    <>
      {spec.navbar === 'site' && <SiteNavbar width={spec.width} />}
      {/* Mobile only, and above `main` in the flow — the band is `sticky`, so it
          needs no `pt-*` spacer the way the `fixed` tab bar needs `pb-16`. What
          it DOES need is for a bounded `main` to subtract its height, which
          `fixedViewportClassName` derives from this same column.

          `=== 'back'` and not `backTarget !== null`: a `page` row resolves a
          target too — the canvas's own bar draws the control — and rendering the
          shell's band there would be the second-band defect §2.1 closed.

          `band !== null` is the same test one column over and carries no second
          decision: `pageBandTitle` returns `null` for exactly the rows this
          `=== 'back'` excludes, and THROWS rather than returning `null` for a row
          that draws the band. */}
      {spec.mobileHeader === 'back' && backTarget !== null && band !== null && (
        <MobileTopBar backTo={backTarget} title={band.title} subtitle={band.subtitle} />
      )}
      {/* Not a band at an edge — a DECLARATION React 19 hoists into <head>,
          which the OS then draws its own dismissible banner from. It is in the
          table because the question it answers ("which pages promote the
          app?") is the same question every other column answers, and the
          answer the owner asked for is "the root surfaces, not every page". */}
      {spec.appBanner && <SmartAppBannerMeta />}
      {/* The resolved target is published to the whole page, not only consumed
          by the band: a `page` row draws its own control and must not decide
          where it goes. See `PageBackTarget.tsx` for why this is a context of
          ours and not `<Outlet context>`. */}
      <PageBackTargetProvider value={backTargetBox}>
        <main className={pageChromeMainClassName(spec)}>
          <Outlet />
        </main>
      </PageBackTargetProvider>
      {/* Desktop only, exactly as all eleven layouts spelled it by hand. */}
      {spec.footer && (
        <div className="hidden md:block">
          <Footer width={spec.width} />
        </div>
      )}
      {spec.bottomTabs && <MobileBottomNav />}
    </>
  );
}
